import { inject, Injectable } from '@angular/core';
import { ProfileSelectorDetails } from '@com-delta-idp/mnx-sitewide-shared-core-ui';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { concatMap, takeUntil } from 'rxjs/operators';
import { ProfileSelectorAdapterService } from '../../adapters/profile-selector/profile-selector-adapter.service';
import { SkyBonusNuTravelAdapterService } from '../../adapters/profile-selector/skybonus-nutravel-adapter/skybonus-nutravel-adapter.service';
import {
  DashboardDataResponse,
  DashboardResponse,
  loginAndGetDashboardResponse,
  OauthAEMConfig
} from '../../models/login';
import { UserSessionService } from '../../services/login/data-services/user-session/user-session.service';
import { LogoutService } from '../../services/logout/logout.service';
import { ProfileSelectorService } from '../../services/profile-selector/data-services/profile-selector.service';
import { ProfileSelectorUtilService } from '../../utils/profile-selector/profile-selector-util.service';
import { AdobeDataLayerService } from '../../services/utils';

@Injectable({
  providedIn: 'root'
})
export class ProfileSelectorFacadeBaseService {
  profileSelectorAdapterService = inject(ProfileSelectorAdapterService);
  profileSelectorContentService = inject(ProfileSelectorService);
  profileSelectorUtilService = inject(ProfileSelectorUtilService);
  userSession = inject(UserSessionService);
  logOut = inject(LogoutService);
  skyBonusNuTravelAdapterService = inject(SkyBonusNuTravelAdapterService);
  adobeDataLayer = inject(AdobeDataLayerService);
  profileDetails$ = new BehaviorSubject<ProfileSelectorDetails | null>(null);
  displayTermsUse$ = new BehaviorSubject<boolean>(false);
  fromProfileStandAlone$ = new BehaviorSubject<boolean>(false);
  isDashBoardResponse$ = new BehaviorSubject<boolean>(false);
  isLoggedInStatus$ = new BehaviorSubject<boolean | undefined>(false);
  personalSelectedState$ = new BehaviorSubject<boolean>(false);
  businessSelectedState$ = new BehaviorSubject({
    selectedState: false,
    accountIndex: 0
  });

  unsubscribe$ = new Subject();
  authConfig = {} as OauthAEMConfig;
  dashboardData = {} as loginAndGetDashboardResponse | DashboardResponse;
  refreshURL = '';
  queryParams = {} as { [key: string]: string };
  paramsToIgnore: string[] = [];
  pingDomain = '';
  queryParamKeys: string[] = [];
  logoutPingUrl = '';
  storeCompanyId$ = new BehaviorSubject<boolean>(false);

  assignQueryParams(queryParams: { [key: string]: string }): void {
    this.queryParams = queryParams;
    this.queryParamKeys = Object.keys(queryParams);
    this.paramsToIgnore = [
      'login_tokenId',
      'login_username',
      'login_lastName',
      'login_password',
      'login_refreshURL',
      'refreshURL',
      'login_isPersistent',
      'login_reasonCode',
      'login_maskedEmail'
    ];
    this.refreshURL = this.getRefreshURL(this.refreshURL);
  }

  getRefreshURL(refreshURL: string): string {
    const contextRoot = '/home';
    const homePageUrl = '/';
    if (
      this.checkRefreshUrl(refreshURL) &&
      this.checkRefreshUrlIndex(refreshURL, contextRoot)
    ) {
      return refreshURL;
    }
    return homePageUrl;
  }

  checkRefreshUrl(refreshURL: string | null): boolean {
    return Boolean(refreshURL) && refreshURL !== '' && refreshURL !== 'null';
  }

  checkRefreshUrlIndex(refreshURL: string, contextRoot: string): boolean {
    return refreshURL !== null && refreshURL.indexOf(contextRoot) === -1;
  }

  logout(): void {
    this.logOut.logOut();
  }

  setLoginStatus(
    res: loginAndGetDashboardResponse | DashboardDataResponse
  ): void {
    this.isLoggedInStatus$.next(res.loggedIn || res.isWarmState);
    this.isDashBoardResponse$.next(true);
    this.dashboardData = res;
  }

  getAccountTypeObservable(
    accountType: string,
    corporateId: string
  ): Observable<string> {
    if (!this.profileSelectorAdapterService.postCorporateFlag) {
      return this.profileSelectorContentService.postAccountTypeCall(
        accountType
      );
    }

    return this.profileSelectorContentService
      .postStoreCorporateCompanyId(corporateId)
      .pipe(
        takeUntil(this.unsubscribe$),
        concatMap(() =>
          this.profileSelectorContentService.postAccountTypeCall(accountType)
        )
      );
  }
}
